/**
 * safeAutoFocusRef – sicherer Ersatz für das native React-`autoFocus`-Prop.
 *
 * WARUM (Bug iPad/Mobile-Safari, Juni 2026):
 *   Das `autoFocus`-Prop lässt React `node.focus()` SYNCHRON in der
 *   commitMount-Phase aufrufen (Stacktrace: `focus@[native code]` ← `commitMount`
 *   ← `commitHostMount` ← `commitLayoutEffectOnFiber`). Auf iPad / Mobile-Safari
 *   (WebKit) führt dieser synchrone, programmatische Fokus während des Mounts zu
 *   einem unbehandelten Runtime-Error, der als rotes „Uncaught runtime errors"-
 *   Overlay erscheint (cross-origin sanitisiert: „Script error.").
 *
 * LÖSUNG:
 *   Statt `autoFocus` als Callback-Ref `ref={safeAutoFocusRef}` verwenden.
 *   Der Fokus wird NACH dem Paint (requestAnimationFrame), guarded und in
 *   try/catch gekapselt gesetzt – Fokus ist UX, nicht kritisch. Die Funktion ist
 *   modul-global (stabile Identität) → React ruft sie nur bei Mount/Unmount auf,
 *   nie bei jedem Render (kein Fokus-Klau während der Eingabe).
 */
export const safeAutoFocusRef = (node) => {
  if (!node) return;
  const schedule =
    typeof window !== "undefined" &&
    typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame.bind(window)
      : (cb) => setTimeout(cb, 0);
  schedule(() => {
    try {
      if (
        typeof document !== "undefined" &&
        document.contains(node) &&
        !node.disabled &&
        typeof node.focus === "function"
      ) {
        node.focus();
      }
    } catch {
      /* iPad/WebKit kann beim programmatischen Fokus werfen – bewusst ignorieren. */
    }
  });
};
